/**
 * Stock Item Duplication Logic
 * Handles validation and preparation for duplicating stock items
 */

import { StockItem } from '@/types/stock-item';

export interface DuplicateItem {
  original: StockItem;
  suggestedCode: string;
  newCode: string;
  isActive: boolean;
  error?: string;
}

export interface DuplicateValidationResult {
  canProceed: boolean;
  items: DuplicateItem[];
  warnings: string[];
  errors: string[];
}

/**
 * Generate a suggested stock code for duplicate
 */
export function generateDuplicateCode(originalCode: string, existingCodes: string[]): string {
  // Try with -COPY suffix first
  let suggestedCode = `${originalCode}-COPY`;
  
  // If that exists, try with numbers
  if (existingCodes.includes(suggestedCode)) {
    let counter = 1;
    while (existingCodes.includes(`${originalCode}-COPY${counter}`)) {
      counter++;
    }
    suggestedCode = `${originalCode}-COPY${counter}`;
  }
  
  return suggestedCode;
}

/**
 * Validate items for duplication
 */
export function validateDuplication(
  selectedItems: StockItem[],
  allStockCodes: string[]
): DuplicateValidationResult {
  const warnings: string[] = [];
  const errors: string[] = [];
  const items: DuplicateItem[] = [];

  if (selectedItems.length === 0) {
    errors.push('No items selected for duplication');
    return { canProceed: false, items: [], warnings, errors };
  }

  // Process each item
  selectedItems.forEach((item) => {
    const suggestedCode = generateDuplicateCode(item.code, allStockCodes);
    
    const duplicateItem: DuplicateItem = {
      original: item,
      suggestedCode,
      newCode: suggestedCode,
      isActive: true, // Default to active
    };

    // Add warnings for items with special conditions
    if (item.inactive) {
      warnings.push(`${item.code} is inactive - duplicate will be created as active`);
    }

    if (item.physicalQuantity > 0) {
      warnings.push(`${item.code} has quantity ${item.physicalQuantity} - duplicate will start with 0`);
    }

    if (item.totalValueOfSales > 0 || item.totalCostOfSales > 0) {
      warnings.push(`${item.code} has transaction history - duplicate will have clean history`);
    }

    items.push(duplicateItem);
  });

  return {
    canProceed: errors.length === 0,
    items,
    warnings,
    errors,
  };
}

/**
 * Prepare duplicate data from original stock item
 * Copies relevant fields and resets quantities/history
 */
export function prepareDuplicateData(
  original: StockItem,
  newCode: string,
  isActive: boolean
): Partial<StockItem> {
  return {
    // New identifiers
    code: newCode,
    barcode: '', // Leave empty - user can set later
    
    // Copy basic info
    description: original.description,
    category: original.category,
    unitOfMeasure: original.unitOfMeasure,
    
    // Copy pricing (including cost prices that should be duplicated)
    unitPrice: original.unitPrice,
    averageCost: original.averageCost,
    lastCostPrice: original.lastCostPrice,
    value: original.value, // TotalValue in database - copy for duplication
    lastPurchasePrice: original.lastPurchasePrice,
    lastSalePrice: original.lastSalePrice,
    
    // Copy location
    warehouseCode: original.warehouseCode,
    locationCode: original.locationCode,
    
    // Copy supplier info
    supplier: original.supplier,
    leadTimeDays: original.leadTimeDays,
    supplierContact: original.supplierContact,
    
    // Copy reorder settings
    reorderPoint: original.reorderPoint,
    reorderQuantity: original.reorderQuantity,
    minQuantity: original.minQuantity,
    maxQuantity: original.maxQuantity,
    
    // Copy packaging
    packSize: original.packSize,
    cartonQuantity: original.cartonQuantity,
    weightPerUnit: original.weightPerUnit,
    weightPerUnitUnit: original.weightPerUnitUnit,
    dimensionLength: original.dimensionLength,
    dimensionWidth: original.dimensionWidth,
    dimensionHeight: original.dimensionHeight,
    dimensionUnit: original.dimensionUnit,
    cubicVolume: original.cubicVolume,
    
    // Copy analysis codes
    purchaseAnalysisCode: original.purchaseAnalysisCode,
    salesAnalysisCode: original.salesAnalysisCode,
    movementInAnalysis: original.movementInAnalysis,
    movementOutAnalysis: original.movementOutAnalysis,
    
    // Copy weight
    weight: original.weight,
    weightUnit: original.weightUnit,
    
    // Copy bin number
    binNumber: original.binNumber,
    
    // Set status (inactive = false means active)
    inactive: !isActive,
    
    // Reset quantities to zero
    physicalQuantity: 0,
    allocatedQuantity: 0,
    quantityDueIn: 0,
    
    // Reset transaction history
    totalCostOfSales: 0,
    totalValueOfSales: 0,
    
    // Reset ABC classification and stock turn
    abcClassification: 'C' as const,
    stockTurnRate: 0,
    
    // Clear dates
    lastMovementDate: new Date(),
    lastPurchaseDate: null,
    lastSaleDate: null,
    
    // Reset performance metrics
    lastPurchaseQuantity: undefined,
    lastSaleQuantity: undefined,
    onTimeDeliveryRate: undefined,
    averageLeadTime: undefined,
    totalOrdersYTD: undefined,
  };
}

/**
 * Validate that a new code is unique and valid
 */
export function validateNewCode(
  newCode: string,
  existingCodes: string[],
  otherNewCodes: string[]
): { isValid: boolean; error?: string } {
  // Check if empty
  if (!newCode || newCode.trim() === '') {
    return { isValid: false, error: 'Stock code cannot be empty' };
  }

  // Check if too long
  if (newCode.length > 50) {
    return { isValid: false, error: 'Stock code must be 50 characters or less' };
  }

  // Check if already exists
  if (existingCodes.includes(newCode)) {
    return { isValid: false, error: 'Stock code already exists' };
  }

  // Check if duplicate in current batch
  if (otherNewCodes.includes(newCode)) {
    return { isValid: false, error: 'Duplicate code in this batch' };
  }

  // Check for invalid characters (optional - adjust based on your rules)
  const validCodePattern = /^[A-Z0-9\-_]+$/i;
  if (!validCodePattern.test(newCode)) {
    return { isValid: false, error: 'Stock code can only contain letters, numbers, hyphens, and underscores' };
  }

  return { isValid: true };
}

/**
 * Get a summary of what will be copied vs reset
 */
export function getDuplicationSummary() {
  return {
    copied: [
      'Description',
      'Category',
      'Unit of Measure',
      'Unit Price',
      'Cost Prices (Average, Last, Standard)',
      'Location (Warehouse & Location Code)',
      'Supplier Information',
      'Reorder Settings',
      'Packaging Information',
      'Analysis Codes',
      'Notes',
    ],
    reset: [
      'Stock Code (new code generated)',
      'Barcode (cleared)',
      'All Quantities (set to 0)',
      'Transaction History (cleared)',
      'Movement Dates (cleared)',
      'Sale/Purchase Dates (cleared)',
    ],
    notes: [
      'The duplicate will be created as a new, independent stock item',
      'No transaction history will be copied',
      'You can edit the new stock code before creating',
      'The duplicate will be created as active by default',
    ],
  };
}
